// FrameItForwardEdit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from '../../../public/icons';
import { MinusIcon, PlusIcon, GiftIcon } from '../../../public/icons';
import ReviewModal from '../../../component/general/ReviewModal';
import CustomerReviews from '../../../component/general/CustomerReviews';
import GiftBoxCustomizeModal from '../../../component/frameitforward/GiftBoxCustomizeModal';
import ExtrasModal from '../../../component/frameitforward/ ExtrasModal';
import FutureGiftModal from '../../../component/frameitforward/FutureGiftModal';
import OrderSummaryModal from '../../../component/frameitforward/OrderSummaryModal';

/* ─────────────────────────────────────────────
   Draft persistence helpers
   ───────────────────────────────────────────── */
const DRAFT_PREFIX = 'kapta_draft_';
const MODAL_DRAFT_KEY = 'kapta_frameitforward_modal_draft';

const loadDraft = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load draft:', err);
    return null;
  }
};

const saveDraft = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save draft:', err);
  }
};

const clearDraftByKey = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.error('Failed to clear draft:', err);
  }
};

/* ─────────────────────────────────────────────
   Step keys inside the modal draft
   ───────────────────────────────────────────── */
const STEP_KEYS = {
  customize: 'kapta_frameitforward_step_customize',
  extras: 'kapta_frameitforward_step_extras',
  futureGift: 'kapta_frameitforward_step_futuregift',
};

const loadStepDraft = (stepKey) => {
  try {
    const raw = localStorage.getItem(stepKey);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error(`Failed to load step draft ${stepKey}:`, err);
    return null;
  }
};

const saveStepDraft = (stepKey, data) => {
  try {
    localStorage.setItem(stepKey, JSON.stringify(data));
  } catch (err) {
    console.error(`Failed to save step draft ${stepKey}:`, err);
  }
};

const clearStepDrafts = () => {
  Object.values(STEP_KEYS).forEach((k) => localStorage.removeItem(k));
};

const FrameItForwardEdit = () => {
  const location = useLocation();

  /* ── Stable draft key from URL (survives reload) ── */
  const draftKey =
    `${DRAFT_PREFIX}${location.pathname
      .replace(/^\//, '')
      .replace(/\//g, '-')
      .replace(/-+/g, '-')}` ||
    `${DRAFT_PREFIX}frame-it-forward_default`;

  // Product page state
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Modal flow state
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showExtrasModal, setShowExtrasModal] = useState(false);
  const [showFutureGiftModal, setShowFutureGiftModal] = useState(false);
  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Step data
  const [customizationData, setCustomizationData] = useState(null);
  const [extrasData, setExtrasData] = useState(null);
  const [futureGiftData, setFutureGiftData] = useState(null);

  // Refresh key for CustomerReviews
  const [reviewsRefreshKey, setReviewsRefreshKey] = useState(0);

  // Loading state for Add to Bag
  const [isAdding, setIsAdding] = useState(false);

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const draftHydratedRef = useRef(false);

  /* ─────────────────────────────────────────────
     1. HYDRATE from localStorage on mount
     ───────────────────────────────────────────── */
  useEffect(() => {
    const draft = loadDraft(draftKey);
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.quantity === 'number') setQuantity(draft.quantity);
    if (typeof draft.currentImageIndex === 'number')
      setCurrentImageIndex(draft.currentImageIndex);
    if (draft.customizationData !== undefined)
      setCustomizationData(draft.customizationData);
    if (draft.extrasData !== undefined) setExtrasData(draft.extrasData);
    if (draft.futureGiftData !== undefined)
      setFutureGiftData(draft.futureGiftData);

    draftHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  /* ─────────────────────────────────────────────
     2. AUTOSAVE on every change
     ───────────────────────────────────────────── */
  useEffect(() => {
    if (!draftHydratedRef.current) return;

    saveDraft(draftKey, {
      quantity,
      currentImageIndex,
      customizationData,
      extrasData,
      futureGiftData
    });
  }, [
    draftKey,
    quantity,
    currentImageIndex,
    customizationData,
    extrasData,
    futureGiftData
  ]);

  /* Save on refresh / tab close as belt-and-suspenders */
  useEffect(() => {
    const saveNow = () => {
      if (!draftHydratedRef.current) return;
      saveDraft(draftKey, {
        quantity,
        currentImageIndex,
        customizationData,
        extrasData,
        futureGiftData
      });
    };
    window.addEventListener('beforeunload', saveNow);
    return () => window.removeEventListener('beforeunload', saveNow);
  }, [
    draftKey,
    quantity,
    currentImageIndex,
    customizationData,
    extrasData,
    futureGiftData
  ]);

  const productImages = [
    'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800&auto=format&fit=crop&q=60',
    'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&auto=format&fit=crop&q=60',
    'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w-800&auto=format&fit=crop&q=60',
    'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800&auto=format&fit=crop&q=60'
  ];

  const title = 'Frame It Forward';
  const basePrice = 320000;
  const inStock = true;
  const PRODUCT_SLUG = 'frame-it-forward';

  /* ───────── Toast helper ───────── */
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  /* ───────── Step handlers ───────── */
  const handleGiftBoxCustomize = (data) => {
    setCustomizationData(data);
    setShowCustomizeModal(false);
    setShowExtrasModal(true);
  };

  const handleExtrasContinue = (data) => {
    setExtrasData(data);
    setShowExtrasModal(false);
    setShowFutureGiftModal(true);
  };

  const handleExtrasBack = () => {
    setShowExtrasModal(false);
    setShowCustomizeModal(true);
  };

  const handleFutureGiftContinue = (data) => {
    setFutureGiftData(data);
    setShowFutureGiftModal(false);
    setShowOrderSummaryModal(true);
  };

  const handleFutureGiftBack = () => {
    setShowFutureGiftModal(false);
    setShowExtrasModal(true);
  };

  const handleOrderSummaryBack = () => {
    setShowOrderSummaryModal(false);
    setShowFutureGiftModal(true);
  };

  /* ───────── Reset the modal flow ───────── */
  const resetFlow = () => {
    setCustomizationData(null);
    setExtrasData(null);
    setFutureGiftData(null);
    setQuantity(1);
  };

  /* ───────── Total price calc ───────── */
  const computeTotal = () => {
    const extrasPrice =
      extrasData?.selectedExtras?.reduce((sum, id) => {
        if (id === 'greeting-card') return sum + 5000;
        if (id === 'wine') return sum + 8000;
        if (id === 'audio-gift') return sum + 20000;
        return sum;
      }, 0) || 0;

    const futureGiftPrice = futureGiftData?.futureGifts?.length
      ? futureGiftData.futureGifts.length * 20000
      : 0;

    return basePrice + extrasPrice + futureGiftPrice;
  };

  /* ───────── Add to bag ───────── */
  const handleAddToBag = async () => {
    if (isAdding) return;

    if (!inStock) {
      showToast('This item is out of stock', 'error');
      return;
    }

    try {
      setIsAdding(true);

      await new Promise((resolve) => setTimeout(resolve, 400));

      const total = computeTotal();

      const cartItem = {
        cartItemId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id: PRODUCT_SLUG,
        slug: `${PRODUCT_SLUG}-custom`,
        title: `${title} - Custom Gift Box`,
        description: 'A memory gift you give once and continue over time.',
        isCustom: true,
        quantity,
        price: total,
        lineTotal: total * quantity,
        image: productImages[0],
        customization: {
          ...customizationData,
          extras: extrasData,
          futureGift: futureGiftData
        }
      };

      const existing = JSON.parse(
        localStorage.getItem('memoryMagnetsCart') || '[]'
      );
      existing.push(cartItem);
      localStorage.setItem('memoryMagnetsCart', JSON.stringify(existing));

      /* 🟢 Clear ALL drafts — committed to cart */
      clearDraftByKey(draftKey);
      localStorage.removeItem(MODAL_DRAFT_KEY);
      clearStepDrafts();

      /* 🟢 Notify the app + open the shopping bag */
      window.dispatchEvent(new Event('cartUpdated'));
      window.dispatchEvent(new CustomEvent('openShoppingBag'));

      // Toast + close modal + reset
      showToast('Added to bag', 'success');
      setShowOrderSummaryModal(false);
      resetFlow();
    } catch (err) {
      console.error('Failed to add to bag:', err);
      showToast('Failed to add to bag. Please try again.', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  /* ───────── Image navigation ───────── */
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  /* ───────── Progress bridge for step modals ─────────
     These fire whenever the user changes something in a step modal.
     We persist into the outer draft so nothing is lost on reload. */
  const handleCustomizeProgress = (progress) => {
    if (!progress) return;
    saveStepDraft(STEP_KEYS.customize, progress);
  };

  const handleExtrasProgress = (progress) => {
    if (!progress) return;
    saveStepDraft(STEP_KEYS.extras, progress);
  };

  const handleFutureGiftProgress = (progress) => {
    if (!progress) return;
    saveStepDraft(STEP_KEYS.futureGift, progress);
  };

  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      {/* ───────── Toast Popup ───────── */}
      {toast.show && (
        <div
          className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999]"
          style={{ animation: 'fadeInDown 0.3s ease-out' }}
        >
          <div
            className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-xl border ${
              toast.type === 'success'
                ? 'bg-[#E7F4ED] border-[#2F8F5B] text-[#2F8F5B]'
                : toast.type === 'error'
                ? 'bg-[#F4E7E7] border-[#A82121] text-[#A82121]'
                : 'bg-[#E7ECF4] border-[#2F578F] text-[#2F578F]'
            }`}
          >
            {toast.type === 'success' && (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="#2F8F5B" strokeWidth="2" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="#2F8F5B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span className="font-medium text-sm sm:text-base">
              {toast.message}
            </span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 max-w-[85rem] pt-20 sm:pt-24 md:pt-28 lg:pt-32">
        <div className="flex flex-col lg:flex-row justify-between gap-6 md:gap-8 lg:gap-10">
          {/* Left Column - Image Gallery */}
          <div className="w-full lg:w-1/2">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex flex-row sm:flex-col gap-2 sm:gap-2.5 order-2 sm:order-1">
                {productImages.map((image, index) => (
                  <div
                    key={index}
                    className={`w-14 h-14 sm:w-16 sm:h-16 cursor-pointer border-2 flex-shrink-0 ${
                      currentImageIndex === index
                        ? 'border-[#D2A146]'
                        : 'border-transparent'
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>

              <div className="relative overflow-hidden order-1 sm:order-2 flex-1">
                <img
                  src={productImages[currentImageIndex]}
                  alt={title}
                  className="w-full max-w-[498px] h-[300px] sm:h-[400px] md:h-[500px] lg:h-[632px] object-cover mx-auto"
                />

                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-[#FCF7EE] hover:bg-[#E8E0D5] flex items-center justify-center transition-colors rounded-full border border-[#DBDBDB]"
                >
                  <ArrowLeft size={16} color="#1C2A46" />
                </button>

                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-[#FCF7EE] hover:bg-[#E8E0D5] flex items-center justify-center transition-colors rounded-full border border-[#DBDBDB]"
                >
                  <ArrowRight size={16} color="#1C2A46" />
                </button>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="w-full lg:w-1/2">
            <div className="space-y-6 sm:space-y-8 text-left">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C2A46] mb-2">
                  Frame It Forward
                </h1>
                <p className="font-light text-xs sm:text-sm text-[#55000C] mb-2">
                  A memory gift you give once and continue over time.
                </p>
                <p className="text-sm sm:text-base md:text-lg text-[#6B635D] leading-relaxed">
                  Frame It Forward is Kapta's signature gift experience built
                  around a framed memory magnet, with the option to pre-pay and
                  prepare additional future gifts that will be fulfilled later.
                </p>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-medium text-[#1C2A46] mb-2">
                  What Included
                </h3>
                <div className="bg-[#F5F5F5] px-3 sm:px-4 py-4 sm:py-5">
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        Memory Magnet Set
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        A Frame
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        QR "Gift Portal"
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        Optional keepsake add-ons (teddy, wine, notes etc.)
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center justify-between space-x-4 w-32 sm:w-36 p-1.5 border border-[#E0E0E0]">
                    <button
                      onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                      className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                      disabled={isAdding}
                    >
                      <MinusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                    </button>
                    <span className="text-sm sm:text-base font-normal text-[#1C2A46] min-w-[2.5rem] sm:min-w-[3rem] text-center">
                      {quantity} set
                    </span>
                    <button
                      onClick={() => setQuantity((prev) => prev + 1)}
                      className="rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                      disabled={isAdding}
                    >
                      <PlusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 mt-5">
                  <div className="mb-4 sm:mb-6">
                    <span className="text-2xl sm:text-3xl font-medium text-[#1C2A46]">
                      ₦{basePrice.toLocaleString()}
                    </span>
                    <div className="text-xs sm:text-sm text-[#6B635D] mt-1">
                      Base box price
                    </div>
                  </div>

                  <div className="flex-1">
                    <button
                      onClick={() => setShowCustomizeModal(true)}
                      disabled={!inStock || isAdding}
                      className={`py-3 sm:py-4 text-base sm:text-lg font-medium w-full sm:w-auto sm:min-w-[280px] md:w-[393px] transition-colors flex items-center justify-center space-x-2 ${
                        !inStock || isAdding
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-[#D2A146] text-[#FCF7EE] hover:bg-[#C4956A]'
                      }`}
                    >
                      <GiftIcon />
                      <span>Customise Your Giftbox</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="text-left mt-12 sm:mt-16 md:mt-24 lg:mt-32 mb-6 sm:mb-8">
          <h3 className="text-2xl sm:text-3xl font-medium text-[#1C2A46] mb-6 sm:mb-8 border-b-2 pb-4 sm:pb-6 border-bordercolor">
            Product Details
          </h3>
          <p className="text-sm sm:text-base text-[#6B635D] mb-6 sm:mb-8 leading-relaxed">
            Frame It Forward is more than just a gift - it's an experience that
            unfolds over time. It begins with a beautifully framed memory magnet
            that tells a meaningful story. Each magnet features a custom photo on
            the front and a personal story printed on the back. The frame comes
            with a QR code that unlocks a digital gift portal, allowing you to
            schedule and deliver additional future gifts on special dates like
            anniversaries, birthdays, or holidays.
          </p>

          <h4 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-3 sm:mb-4">
            How It Works
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-[#F5F5F5] p-4 sm:p-6 rounded-lg">
              <div className="text-xl sm:text-2xl font-bold text-[#D2A146] mb-2">1</div>
              <h5 className="font-medium text-[#1C2A46] mb-2 text-sm sm:text-base">
                Create Your First Gift
              </h5>
              <p className="text-xs sm:text-sm text-[#6B635D]">
                Choose photos, write stories, and select add-ons for your initial
                gift box.
              </p>
            </div>
            <div className="bg-[#F5F5F5] p-4 sm:p-6 rounded-lg">
              <div className="text-xl sm:text-2xl font-bold text-[#D2A146] mb-2">2</div>
              <h5 className="font-medium text-[#1C2A46] mb-2 text-sm sm:text-base">
                Schedule Future Gifts
              </h5>
              <p className="text-xs sm:text-sm text-[#6B635D]">
                Plan additional gifts to be delivered on future dates - perfect
                for anniversaries or milestones.
              </p>
            </div>
            <div className="bg-[#F5F5F5] p-4 sm:p-6 rounded-lg">
              <div className="text-xl sm:text-2xl font-bold text-[#D2A146] mb-2">3</div>
              <h5 className="font-medium text-[#1C2A46] mb-2 text-sm sm:text-base">
                Unfold Over Time
              </h5>
              <p className="text-xs sm:text-sm text-[#6B635D]">
                Your recipient receives thoughtful gifts throughout the year,
                keeping memories alive.
              </p>
            </div>
          </div>

          <h4 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-3 sm:mb-4">
            Specifications
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 text-sm">
            <div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Magnet Count
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  4 pieces per set
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Frame Size
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  8" x 10"
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Material
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Premium wood frame, vinyl magnets
                </span>
              </div>
            </div>
            <div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Finish
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Matte, water-resistant
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Gift Portal Access
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Lifetime access
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Made In
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Nigeria
                </span>
              </div>
            </div>
          </div>
        </div>

        <CustomerReviews
          slug={PRODUCT_SLUG}
          onWriteReview={() => setShowReviewModal(true)}
          refreshKey={reviewsRefreshKey}
        />
      </div>

      {/* Modal flow */}
      <GiftBoxCustomizeModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onContinue={handleGiftBoxCustomize}
        onProgressChange={handleCustomizeProgress}
      />

      <ExtrasModal
        isOpen={showExtrasModal}
        onClose={() => setShowExtrasModal(false)}
        onBack={handleExtrasBack}
        onContinue={handleExtrasContinue}
        onProgressChange={handleExtrasProgress}
      />

      <FutureGiftModal
        isOpen={showFutureGiftModal}
        onClose={() => setShowFutureGiftModal(false)}
        onBack={handleFutureGiftBack}
        onContinue={handleFutureGiftContinue}
        onProgressChange={handleFutureGiftProgress}
      />

      <OrderSummaryModal
        isOpen={showOrderSummaryModal}
        onClose={() => setShowOrderSummaryModal(false)}
        onBack={handleOrderSummaryBack}
        onAddToBag={handleAddToBag}
        onRemoveFromBag={() => {}}
        isInCart={false}
        isAdding={isAdding}
        customizationData={customizationData}
        extrasData={extrasData}
        futureGiftData={futureGiftData}
      />

      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setReviewsRefreshKey((k) => k + 1);
        }}
        product={{
          id: 'frame-it-forward',
          title: title,
          image: productImages[0],
          slug: PRODUCT_SLUG,
          price: basePrice,
          category: 'Gift Experience',
          description:
            "Kapta's signature framed memory magnet gift experience",
          inStock: inStock
        }}
      />

      <style jsx global>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default FrameItForwardEdit;